import { LimitOrderSidePanel } from 'components/secondaryMarket/limitOrder/sidePanel/LimitOrderSidePanel';
import type { MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import type { FormOwnProps } from 'shared/connect/connect';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { LimitOrderForm } from 'state/secondaryMarket/limit-order/form/input/inputForm.model';
import { limitOrderKycSelectors } from 'state/secondaryMarket/limit-order/kyc/kyc.selectors';
import type { LimitOrderStatus } from 'state/secondaryMarket/limit-order/limitOrder.model';
import { limitOrderSelectors } from 'state/secondaryMarket/limit-order/limitOrder.selector';
import { limitOrderOrderActions } from 'state/secondaryMarket/limit-order/order/order.action';
import { limitOrderTradeSelectors } from 'state/secondaryMarket/limit-order/order/order.selector';
import { secondaryMarketLimitOrderOrderRequestedThunk } from 'state/secondaryMarket/limit-order/order/order.thunk';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { KycDetail, KycScoreStatus } from 'state/secondaryMarket/shared/kyc/kyc.model';
import { getUserFrontRights } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';

type StateProps = Readonly<{
  errors: readonly SecondaryMarketFormError<LimitOrderForm>[];
  status: LimitOrderStatus;
  errorMessage?: string;
  kycScore: KycScoreStatus | undefined;
  kycDetails: readonly KycDetail[] | undefined;
  timeout?: number;
  canPlaceOrder?: boolean;
  backendReports: readonly SecondaryMarketFormError<LimitOrderForm>[];
}>;

type DispatchProps = Readonly<{
  onTrade: () => void;
  onDone: () => void;
}>;

export type LimitOrderSidePanelProps = FormOwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, FormOwnProps, AppState> = () => {
  const getValidationErrors = limitOrderSelectors.makeGetValidationErrors();
  const getBackendReports = limitOrderSelectors.makeGetBackendReports();
  return (state, { formId }): StateProps => {
    return {
      errors: getValidationErrors(state, formId),
      status: limitOrderSelectors.getOrderStatus(state, formId),
      kycScore: limitOrderKycSelectors.getKycStatusScore(state, formId),
      kycDetails: limitOrderKycSelectors.getKycStatusDetails(state, formId),
      errorMessage: limitOrderTradeSelectors.getTradeErrorMessage(state, formId),
      timeout: limitOrderTradeSelectors.getTradeTimeout(state, formId),
      canPlaceOrder: getUserFrontRights(state).secondaryTradeEnabled,
      backendReports: getBackendReports(state, formId),
    };
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { formId: tabId }: FormOwnProps): DispatchProps => {
  return {
    onTrade: () => {
      logAnalytics.trackEvent('Secondary market', 'Request for limit order trade');
      dispatch(secondaryMarketLimitOrderOrderRequestedThunk(tabId));
    },
    onDone: () => {
      dispatch(limitOrderOrderActions.secondaryMarketOrderCanceled(tabId, 'secondaryMarketQuotes'));
    },
  };
};

export const connectSidePanel = connect(mapStateToProps, mapDispatchToProps);
export const ConnectedLimitOrderSidePanel = connectSidePanel(LimitOrderSidePanel);
